import { Box } from '@material-ui/core';
import React from 'react';
import {
  Slider,
  Rail,
  Handles,
  CustomMode,
  SliderItem
} from 'react-compound-slider';

import { HistogramNode } from '../Histogram';
import SliderRail from './SliderRail';
import SliderHandle from './SliderHandle';

interface RangeSliderProps {
  mode?: 1 | 2 | 3 | CustomMode;
  step?: number;
  domain?: number[];
  displayOrigin?: boolean;
  disabled?: boolean;
  values: readonly number[];
  nodes: HistogramNode[];
  onUpdate?: (values: readonly number[]) => void;
  onChange?: (values: readonly number[]) => void;
}

const RangeSlider: React.VFC<RangeSliderProps> = ({
  mode = 2,
  step = 2,
  domain,
  displayOrigin,
  disabled,
  values,
  nodes,
  onUpdate,
  onChange
}) => {
  const getHandleLabel = (handle: SliderItem, index: number) => {
    const formatMillionNumber = (value: number) => {
      if (value > 1000000000) {
        return `${(value / 1000000000).toLocaleString(undefined, {
          maximumFractionDigits: 1
        })}B`;
      }

      if (value > 1000000) {
        return `${(value / 1000000).toLocaleString(undefined, {
          maximumFractionDigits: 1
        })}M`;
      }

      // Special handling to not format dates. Needs better solution - AWBD-45.
      if (value > 1906 && value < 2023) {
        return value.toString();
      }

      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    if (index === 0) {
      return `${
        displayOrigin || handle.value !== 0
          ? formatMillionNumber(nodes[handle.value].min)
          : 0
      }`;
    }

    return `${formatMillionNumber(nodes[handle.value - 1].max)}${
      handle.value === nodes.length ? '+' : ''
    }`;
  };

  return (
    <Box position="relative" bottom={7}>
      <Slider
        mode={mode}
        step={step}
        disabled={disabled}
        domain={domain}
        onUpdate={onUpdate}
        onChange={onChange}
        values={values}
      >
        <Rail>
          {({ getRailProps }) => (
            <SliderRail disabled={disabled} getRailProps={getRailProps} />
          )}
        </Rail>

        {!disabled && (
          <Handles>
            {({ handles, getHandleProps }) => (
              <React.Fragment>
                {handles.map((handle, index) => (
                  <SliderHandle
                    key={handle.id}
                    handle={handle}
                    value={getHandleLabel(handle, index)}
                    getHandleProps={getHandleProps}
                  />
                ))}
              </React.Fragment>
            )}
          </Handles>
        )}
      </Slider>
    </Box>
  );
};

export default RangeSlider;
